/**
 * Parité avec `frontend/src/model/Config.ts` (portail CodeFirst) : mêmes noms de
 * rôles, clé de session, Keycloak, URL du backend principal. Les cookies listés
 * ici ne sont pas utilisés par l’app doc (authentification via localStorage).
 */
export const CookieName = "Keycloak_Auth";
export const CookieUsername = "Keycloak_Auth_Username";
export const CookieRole = "Keycloak_Auth_Role";
export const CookieEmail = "Keycloak_Auth_Email";
export const AdminRole = "cf-admin";
export const TeacherRole = "cf-teachers";

/** Aligné sur le portail : même clé `authKey` pour un jeton partagé si même origine. */
export const SessionAuthKey = "authKey";
/** Ancienne clé ; lue et migrée vers `SessionAuthKey` au prochain accès. */
const LEGACY_SESSION_AUTH_KEY = "documentation_auth_key";

const kcUrl = process.env.EXPO_PUBLIC_KEYCLOAK_URL || "https://codefirst.iut.uca.fr/keycloak/";
export const KeycloakConfig = {
  url: kcUrl.endsWith("/") ? kcUrl : kcUrl + "/",
  realm: (process.env.EXPO_PUBLIC_KEYCLOAK_REALM as string) || "master",
  clientId: (process.env.EXPO_PUBLIC_KEYCLOAK_CLIENT_ID as string) || "developer-portal-client",
};

/** URL du backend portail (GitLab, etc.) : défauts comme le frontend, surcharge via env. */
function mainBackendBase() {
  const b = process.env.EXPO_PUBLIC_MAIN_BACKEND_BASE || "http://localhost:8080";
  return b.replace(/\/$/, "");
}

export const BaseConfig = {
  get baseUrl() {
    return mainBackendBase();
  },
  get documentationUrl() {
    return (
      process.env.EXPO_PUBLIC_DOCUMENTATION_PORTAL_URL ||
      "https://codefirst.iut.uca.fr/kubernetes/codefirst/cf-documentation"
    );
  },
} as const;

function buildTimeApiBase(): string | undefined {
  const b = process.env.EXPO_PUBLIC_API_BASE;
  if (typeof b === "string" && b.length > 0) {
    return b.replace(/\/$/, "");
  }
  return undefined;
}

/**
 * Résout l'URL de l'API *documentation* (ex. docker 18473), pas celle du backend GitLab 8080.
 * En prod (même site que Nginx) : l’API est publiée sous /documentation-api
 * (ou /beta/documentation-api) — sans ça, le bundle sans EXPO_PUBLIC_API_BASE tombait
 * sur http://localhost:8081 (CORS + mauvaise cible).
 */
export function getApiBase(): string {
  const fromBuild = buildTimeApiBase();
  if (fromBuild) return fromBuild;
  if (typeof window === "undefined" || !window.location) {
    return "http://localhost:8081";
  }
  const { hostname, port, protocol, origin, pathname } = window.location;
  if (port === "18474" || port === "18482") {
    return `${protocol}//${hostname}:18473`;
  }
  if (port === "18484") {
    return `${protocol}//${hostname}:18483`;
  }
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8081";
  }
  const underBeta = pathname.startsWith("/beta/");
  const basePath = underBeta ? "/beta/documentation-api" : "/documentation-api";
  return origin.replace(/\/$/, "") + basePath;
}

export const AppConfig = {
  get apiBase() {
    return getApiBase();
  },
  get backendUrl() {
    return BaseConfig.baseUrl;
  },
  get gitlabUrl() {
    return `${BaseConfig.baseUrl}/gitlab`;
  },
  /** Comme le portail ; en build prod, NODE_ENV=production. */
  get isDevMode() {
    return process.env?.NODE_ENV !== "production";
  },
} as const;

/**
 * Token JWT côté client, avec reprise d’une session enregistrée sous l’ancienne clé.
 */
export function readSessionTokenWithMigration(): string {
  if (typeof localStorage === "undefined") {
    return "";
  }
  const cur = localStorage.getItem(SessionAuthKey);
  if (cur != null && cur !== "") {
    return cur;
  }
  const old = localStorage.getItem(LEGACY_SESSION_AUTH_KEY);
  if (old != null && old !== "") {
    localStorage.setItem(SessionAuthKey, old);
    localStorage.removeItem(LEGACY_SESSION_AUTH_KEY);
    return old;
  }
  return "";
}

/** Même clé secondaire que le portail (`ParseToken`). */
export const KeycloakIdStorageKey = "keycloakID";

export function clearSessionStorageKeys() {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.removeItem(SessionAuthKey);
  localStorage.removeItem(LEGACY_SESSION_AUTH_KEY);
  localStorage.removeItem(KeycloakIdStorageKey);
}
